import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import ProductTemplate from '../../../components/feature/ProductTemplate';
import { useSEO } from '../../../utils/seo';
import { useState } from 'react';

const KitchensPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    quantity: '',
    timeline: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6f4ftbv53dtlm7j9gtg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          quantity: '',
          timeline: '',
          message: '',
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  useSEO({
    title: 'Wholesale Kitchen Cabinets & Countertops | Direct Import Kitchen Packages | Save 20-35%',
    description: 'Direct import wholesale kitchen packages for custom home builders, developers, and contractors. Bulk kitchen cabinets, countertops, appliances, and fixtures with 20-35% cost savings. Factory-direct pricing on complete kitchen solutions for multi-unit projects. Save on building materials with wholesale construction supply.',
    keywords: 'wholesale kitchen cabinets, kitchen countertops bulk, direct import building materials, wholesale construction materials, bulk kitchen supplies, save on building materials, factory direct kitchen packages',
    canonical: `${siteUrl}/products/kitchens`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Premium Kitchen Packages',
      description: 'Wholesale kitchen packages including cabinetry, countertops, appliances, and fixtures. Direct import building materials with 20-35% savings for custom home builders and developers.',
      category: 'Kitchen & Bath',
      brand: {
        '@type': 'Brand',
        name: 'Go Global Inc.',
      },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'CAD',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: 'Go Global Inc.',
        },
      },
    },
  });

  const productData = {
    category: 'Kitchens',
    title: 'Premium Kitchen Packages',
    subtitle: 'Save 20-35% on Complete Kitchen Solutions',
    description: 'Direct import complete kitchen packages including custom cabinetry, countertops, appliances, and fixtures. Perfect for multi-family developments, custom homes, and renovation projects.',
    heroImage: 'https://public.readdy.ai/ai/img_res/84dfc0ee89125d41f6973703c4973789.jpg',
    
    whatIncluded: [
      'Custom cabinetry with soft-close hardware',
      'Countertops (quartz, granite, or solid surface)',
      'Sinks and premium faucets',
      'Backsplash tile materials',
      'Cabinet hardware and accessories',
      'Installation instructions and support',
    ],

    idealFor: [
      'Home builders and custom builders',
      'Condo and apartment developers',
      'Renovation contractors',
      'Property flippers and investors',
      'Hotel and hospitality developers',
    ],

    benefits: [
      {
        icon: 'ri-price-tag-3-line',
        title: 'Save 20-35% on Complete Packages',
        description: 'Direct factory pricing on cabinetry, countertops, and fixtures for multi-unit projects',
      },
      {
        icon: 'ri-palette-line',
        title: 'Custom Design Options',
        description: 'Multiple finishes, styles, and configurations to match your project specifications',
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Quality Assurance',
        description: 'All products meet North American quality and safety standards',
      },
      {
        icon: 'ri-truck-line',
        title: 'Coordinated Delivery',
        description: 'Complete kitchen packages delivered together for streamlined installation',
      },
    ],

    products: [
      {
        name: 'Custom Cabinetry',
        description: 'Solid wood and engineered cabinet systems with soft-close hardware in multiple finishes',
        specs: 'Painted | Stained | Laminate | Soft-close standard',
        image: 'https://readdy.ai/api/search-image?query=premium%20kitchen%20cabinets%20in%20white%20finish%20with%20soft%20close%20hardware%20and%20modern%20design%20displayed%20in%20showroom%20with%20clean%20professional%20lighting&width=600&height=500&seq=kitchen-cabinet-001&orientation=portrait',
      },
      {
        name: 'Countertops',
        description: 'Quartz, granite, marble, and solid surface options from leading global suppliers',
        specs: 'Quartz | Granite | Marble | Solid surface',
        image: 'https://readdy.ai/api/search-image?query=elegant%20quartz%20kitchen%20countertop%20in%20white%20with%20gray%20veining%20showing%20premium%20stone%20surface%20quality%20in%20bright%20clean%20setting&width=600&height=500&seq=kitchen-counter-001&orientation=portrait',
      },
      {
        name: 'Kitchen Appliances',
        description: 'Professional-grade ranges, refrigerators, dishwashers, and ventilation systems',
        specs: 'Energy Star rated | Commercial grade available',
        image: 'https://readdy.ai/api/search-image?query=stainless%20steel%20professional%20kitchen%20appliances%20including%20range%20and%20refrigerator%20in%20modern%20kitchen%20setting%20with%20clean%20design&width=600&height=500&seq=kitchen-appliance-001&orientation=portrait',
      },
      {
        name: 'Sinks & Faucets',
        description: 'Undermount, farmhouse, and commercial sinks with premium faucet systems',
        specs: 'Stainless steel | Composite | Multiple finishes',
        image: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20sink%20with%20chrome%20faucet%20in%20stainless%20steel%20undermount%20style%20showing%20premium%20quality%20fixtures%20in%20bright%20setting&width=600&height=500&seq=kitchen-sink-001&orientation=portrait',
      },
      {
        name: 'Backsplash Tiles',
        description: 'Ceramic, porcelain, glass, and natural stone tile options in various styles',
        specs: 'Subway | Mosaic | Large format | Natural stone',
        image: 'https://readdy.ai/api/search-image?query=elegant%20white%20subway%20tile%20kitchen%20backsplash%20with%20professional%20installation%20showing%20clean%20grout%20lines%20in%20modern%20kitchen&width=600&height=500&seq=kitchen-tile-001&orientation=portrait',
      },
      {
        name: 'Kitchen Hardware',
        description: 'Cabinet pulls, knobs, and hinges in brushed nickel, chrome, and matte black finishes',
        specs: 'Multiple finishes | Soft-close hinges | Modern styles',
        image: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20cabinet%20hardware%20including%20brushed%20nickel%20pulls%20and%20knobs%20displayed%20on%20white%20surface%20with%20professional%20lighting&width=600&height=500&seq=kitchen-hardware-001&orientation=portrait',
      },
    ],

    specifications: [
      'Custom sizing available for all cabinetry',
      'Multiple finish options: painted, stained, laminate',
      'Soft-close drawer and door systems standard',
      'Countertop materials: quartz, granite, marble, solid surface',
      'Energy Star certified appliances available',
      'Commercial-grade options for hospitality projects',
      'Coordinated hardware packages',
      'Installation support available',
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <ProductTemplate data={productData} />
      
      {/* Quote Request Form Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-gray-900 mb-4">
              Get Your Kitchen Package Quote
            </h2>
            <p className="text-lg text-gray-600">
              Tell us about your project and receive a detailed cost comparison within 24 hours
            </p>
          </div>

          <form 
            id="kitchen-quote"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-gray-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select project type</option>
                  <option value="multi-family">Multi-Family Development</option>
                  <option value="custom-home">Custom Home</option>
                  <option value="renovation">Renovation Project</option>
                  <option value="hotel">Hotel/Hospitality</option>
                  <option value="commercial">Commercial Kitchen</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="quantity" className="block text-sm font-semibold text-gray-900 mb-2">
                  Number of Kitchens *
                </label>
                <input
                  type="text"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="e.g., 1, 10, 50 kitchens"
                />
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Timeline *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select timeline</option>
                  <option value="immediate">Immediate (0-3 months)</option>
                  <option value="short">Short-term (3-6 months)</option>
                  <option value="medium">Medium-term (6-12 months)</option>
                  <option value="long">Long-term (12+ months)</option>
                  <option value="planning">Planning phase</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                Project Details
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                placeholder="Describe your kitchen requirements, style preferences, or any specific questions..."
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  ✓ Thank you! We'll send you a detailed quote within 24 hours.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  {errorMessage}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitStatus === 'submitting'}
              className="w-full px-8 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-base font-semibold rounded-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {submitStatus === 'submitting' ? 'Submitting...' : 'Get My Kitchen Package Quote'}
              {submitStatus !== 'submitting' && <i className="ri-arrow-right-line ml-2"></i>}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              We'll respond within 24 hours with a detailed cost comparison
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KitchensPage;